import firebase from "firebase/app";
import { auth, db } from "./config";

/**
 * Ratings and reviews for handymen and agencies — a firebase-v8-compat port of the
 * app's Database/reviews.js `createReviewApi`. Same collection names, same document
 * shape, same doc-id scheme (`{subjectId}__{uid}`), so a review left here shows up in
 * the app and vice versa.
 *
 *   {subject}Reviews/{subjectId}__{uid}   one review per member per subject
 *   {subject}Ratings/{subjectId}          running { count, sum, average }
 */

export const MIN_RATING = 1;
export const MAX_RATING = 5;

const validRating = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n < MIN_RATING || n > MAX_RATING) return null;
  return n;
};

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  return 0;
};

/** Whatever the members collection knows this user as, for signing the review. */
const currentUserName = async (uid) => {
  try {
    const profile = await db.collection("users").doc(uid).get();
    if (profile.exists) {
      const data = profile.data();
      const name = data.username || data.organizationName;
      if (name) return name;
    }
  } catch (e) {
    console.warn("Could not read reviewer profile:", e);
  }

  const user = auth.currentUser;
  return user?.displayName || user?.email?.split("@")[0] || "Rekebisha member";
};

const createReviewApi = ({ reviewsCollection, ratingsCollection }) => {
  const reviewId = (subjectId, uid) => `${subjectId}__${uid}`;

  const shapeReview = (snapshot) => {
    const data = snapshot.data();
    return {
      id: snapshot.id,
      subjectId: data.subjectId,
      userId: data.userId,
      userName: data.userName || "Rekebisha member",
      rating: data.rating ?? 0,
      comment: data.comment || "",
      createdAtMillis: toMillis(data.updatedAt) || toMillis(data.createdAt),
    };
  };

  /**
   * Create or update the signed-in member's review. The aggregate is adjusted by the
   * delta rather than recomputed, so editing a review never double-counts it.
   */
  const submitReview = async ({ subjectId, rating, comment = "" }) => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("You need to be signed in to leave a review.");
    }

    const score = validRating(rating);
    if (score === null) {
      throw new Error("Choose a star rating between 1 and 5.");
    }

    const userName = await currentUserName(user.uid);
    const reviewRef = db.collection(reviewsCollection).doc(reviewId(subjectId, user.uid));
    const ratingRef = db.collection(ratingsCollection).doc(subjectId);
    const timestamp = firebase.firestore.FieldValue.serverTimestamp();

    return db.runTransaction(async (transaction) => {
      const [existingReview, existingRating] = await Promise.all([
        transaction.get(reviewRef),
        transaction.get(ratingRef),
      ]);

      const previousScore = existingReview.exists ? existingReview.data().rating ?? 0 : 0;
      const wasCounted = existingReview.exists;

      const current = existingRating.exists ? existingRating.data() : { count: 0, sum: 0 };

      const count = (current.count ?? 0) + (wasCounted ? 0 : 1);
      const sum = (current.sum ?? 0) - previousScore + score;
      const average = count > 0 ? sum / count : 0;

      transaction.set(
        reviewRef,
        {
          subjectId,
          userId: user.uid,
          userName,
          rating: score,
          comment: comment.trim(),
          updatedAt: timestamp,
          ...(wasCounted ? {} : { createdAt: timestamp }),
        },
        { merge: true }
      );

      transaction.set(
        ratingRef,
        { subjectId, count, sum, average, updatedAt: timestamp },
        { merge: true }
      );

      return { average, count };
    });
  };

  /** Every review for one subject, newest first. */
  const fetchReviews = async (subjectId) => {
    const snapshot = await db
      .collection(reviewsCollection)
      .where("subjectId", "==", subjectId)
      .get();

    return snapshot.docs.map(shapeReview).sort((a, b) => b.createdAtMillis - a.createdAtMillis);
  };

  /** The signed-in member's own review, or null. */
  const fetchMyReview = async (subjectId) => {
    const user = auth.currentUser;
    if (!user) return null;

    const snapshot = await db.collection(reviewsCollection).doc(reviewId(subjectId, user.uid)).get();
    return snapshot.exists ? shapeReview(snapshot) : null;
  };

  return { reviewsCollection, ratingsCollection, submitReview, fetchReviews, fetchMyReview };
};

const handymen = createReviewApi({
  reviewsCollection: "handymanReviews",
  ratingsCollection: "handymanRatings",
});

const agencies = createReviewApi({
  reviewsCollection: "agencyReviews",
  ratingsCollection: "agencyRatings",
});

/** Picks the handyman or agency review API by subject type, for shared UI components. */
export const reviewApiFor = (subjectType) => (subjectType === "agency" ? agencies : handymen);
